import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const hotels = [
  { name: 'Hilton Garden Inn Annapolis', descKey: 'hiltonGardenDesc', price: '$$', distanceMi: 3, url: 'https://www.hilton.com/en/hotels/andmdhx-hampton-suites-annapolis/' },
  { name: 'Crowne Plaza Annapolis', descKey: 'crownePlazaDesc', price: '$$', distanceMi: 3, url: 'https://www.ihg.com/crowneplaza/hotels/us/en/annapolis/anpcp/hoteldetail' },
  { name: 'Courtyard by Marriott Annapolis', descKey: 'courtyardDesc', price: '$$', distanceMi: 4, url: 'https://www.marriott.com/en-us/hotels/bwian-courtyard-annapolis/overview/' },
  { name: 'Holiday Inn Express Annapolis', descKey: 'holidayInnDesc', price: '$$', distanceMi: 4, url: 'https://www.ihg.com/holidayinnexpress/hotels/us/en/annapolis/anprr/hoteldetail' },
  { name: 'Best Western Annapolis', descKey: 'bestWesternDesc', price: '$', distanceMi: 4, url: 'https://www.bestwestern.com/en_US/book/hotel-details.21044.html' },
  { name: 'Hampton Inn & Suites Annapolis', descKey: 'hamptonInnDesc', price: '$$', distanceMi: 4, url: 'https://www.hilton.com/en/hotels/andmdhx-hampton-suites-annapolis/' },
  { name: 'The Westin Annapolis', descKey: 'westinDesc', price: '$$$', distanceMi: 5, url: 'https://www.marriott.com/en-us/hotels/bwiwa-the-westin-annapolis/overview/' },
  { name: 'Graduate by Hilton Annapolis', descKey: 'graduateDesc', price: '$$$', distanceMi: 5, url: 'https://www.hilton.com/en/hotels/anpgagu-graduate-annapolis/' },
  { name: 'Annapolis Waterfront Hotel', descKey: 'waterfrontHotelDesc', price: '$$$$', distanceMi: 4, url: 'https://www.marriott.com/en-us/hotels/anpaw-annapolis-waterfront-hotel-autograph-collection/overview/' },
  { name: 'Loews Annapolis Hotel', descKey: 'loewsDesc', price: '$$$$', distanceMi: 4, url: 'https://www.loewshotels.com/annapolis-hotel' },
  { name: 'Historic Inns of Annapolis', descKey: 'historicInnsDesc', price: '$$$', distanceMi: 4, url: 'https://www.historicinnsofannapolis.com/' },
  { name: 'Wyndham Garden Annapolis', descKey: 'wyndhamDesc', price: '$$', distanceMi: 5, url: 'https://www.wyndhamhotels.com/wyndham-garden/annapolis-maryland/wyndham-garden-annapolis/overview' },
];

const airbnbs = [
  { nameKey: 'airbnbAnnapolisOasis', descKey: 'airbnbOasisDesc', price: '$$$$', guests: 8, bedrooms: 4, distanceMi: 5, url: 'https://www.airbnb.com/annapolis-md/stays/waterfront' },
  { nameKey: 'airbnbEastportGem', descKey: 'airbnbEastportDesc', price: '$$', guests: 4, bedrooms: 2, distanceMi: 5, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbWeemsCottage', descKey: 'airbnbWeemsDesc', price: '$$', guests: 4, bedrooms: 2, distanceMi: 4, url: 'https://www.airbnb.com/annapolis-md/stays/waterfront' },
  { nameKey: 'airbnbBayRidge', descKey: 'airbnbBayRidgeDesc', price: '$$$$', guests: 10, bedrooms: 5, distanceMi: 6, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbWaterfrontSuite', descKey: 'airbnbWaterfrontSuiteDesc', price: '$$', guests: 2, bedrooms: 1, distanceMi: 5, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbAnchorage', descKey: 'airbnbAnchorageDesc', price: '$$$$', guests: 10, bedrooms: 5, distanceMi: 7, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbEastportRetreat', descKey: 'airbnbEastportRetreatDesc', price: '$$$', guests: 6, bedrooms: 3, distanceMi: 5, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbSpaCreekCottage', descKey: 'airbnbSpaCreekDesc', price: '$$', guests: 4, bedrooms: 2, distanceMi: 4, url: 'https://www.airbnb.com/annapolis-md/stays/waterfront' },
  { nameKey: 'airbnbWestStreetLoft', descKey: 'airbnbWestStreetDesc', price: '$$', guests: 3, bedrooms: 1, distanceMi: 4, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbHarbourStudio', descKey: 'airbnbHarbourStudioDesc', price: '$', guests: 2, bedrooms: 1, distanceMi: 4, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbDowntownNook', descKey: 'airbnbDowntownNookDesc', price: '$$', guests: 2, bedrooms: 1, distanceMi: 4, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbSailorsNest', descKey: 'airbnbSailorsNestDesc', price: '$', guests: 2, bedrooms: 1, distanceMi: 5, url: 'https://www.airbnb.com/annapolis-md/stays' },
  { nameKey: 'airbnbCompromiseHideaway', descKey: 'airbnbCompromiseHideawayDesc', price: '$$$', guests: 2, bedrooms: 1, distanceMi: 4, url: 'https://www.airbnb.com/annapolis-md/stays/waterfront' },
];

const priceRank = (price) => (price ? price.length : 0);
const sortByPrice = (arr) => [...arr].sort((a, b) => priceRank(a.price) - priceRank(b.price));
const sortedHotels = sortByPrice(hotels);
const sortedAirbnbs = sortByPrice(airbnbs);

const formatDistance = (mi, language) =>
  language === 'fr' ? `${(mi * 1.60934).toFixed(1)} km` : `${mi} mi`;

const AccommodationCard = ({ name, description, price, distanceMi, url, linkText, guests, bedrooms, t, language }) => (
  <div className="border border-[#CBD5E1] rounded-sm p-5 hover:border-[#1E3A5F] transition-colors bg-white">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div className="flex-1">
        <h4 className="font-serif text-lg text-[#1A2B3D] mb-1">{name}</h4>
        <p className="text-[#6B8299] text-sm mb-3 leading-relaxed">{description}</p>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="flex items-center gap-1 text-[#1E3A5F]">
            <MapPin className="w-3 h-3" /> {formatDistance(distanceMi, language)} {t('fromVenue')}
          </span>
          {price && <span className="text-[#C9A96E] font-medium">{price}</span>}
          {guests && <span className="text-[#6B8299]">{guests} {t('guests')}</span>}
          {bedrooms && <span className="text-[#6B8299]">{bedrooms} {t('bedrooms')}</span>}
        </div>
      </div>
      {url && url !== '#' && (
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 text-[#1E3A5F] text-sm hover:text-[#C9A96E] transition-colors whitespace-nowrap">
          {linkText} <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  </div>
);

export const Accommodations = () => {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeTab, setActiveTab] = useState('hotels');

  const tabs = [
    { id: 'hotels', label: t('hotelsTitle') },
    { id: 'airbnbs', label: t('airbnbsTitle') },
  ];

  return (
    <section ref={ref} className="py-20 bg-white" data-testid="accommodations-section">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }} className="text-center mb-8">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A2B3D] mb-4">{t('whereToStayTitle')}</h2>
          <div className="wave-divider mb-6" />
          <p className="text-[#6B8299] max-w-2xl mx-auto">{t('whereToStayDesc')}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }} className="flex justify-center gap-2 mb-8">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 text-sm rounded-full transition-all ${
                activeTab === tab.id
                  ? 'bg-[#1E3A5F] text-white'
                  : 'bg-[#EFF4F9] text-[#6B8299] hover:text-[#1A2B3D]'
              }`} data-testid={`tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }} className="space-y-4">
          {activeTab === 'hotels' && sortedHotels.map((item) => (
            <AccommodationCard key={item.name} name={item.name} description={t(item.descKey)}
              price={item.price} distanceMi={item.distanceMi} url={item.url} linkText={t('visitWebsite')} t={t} language={language} />
          ))}
          {activeTab === 'airbnbs' && sortedAirbnbs.map((item) => (
            <AccommodationCard key={item.nameKey} name={t(item.nameKey)} description={t(item.descKey)}
              price={item.price} distanceMi={item.distanceMi} url={item.url} linkText={t('viewOnAirbnb')}
              guests={item.guests} bedrooms={item.bedrooms} t={t} language={language} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
